// @givmo/org-console — the INVITE COMPOSER: offer someone administration, and say what they get.
//
// ══ WHY THIS IS ONE ACT AND NOT TWO ════════════════════════════════════════════════════════════
//
// Under the invitation model a non-Global-Admin holds exactly what they were invited into — nothing
// fans out behind it — so "invite them" and "decide what they can open" are the same decision, made
// once, before anybody is in. A composer that took only an address would create administrators who
// hold nothing and then require a second visit to a second screen to make them useful, which is how
// the estate ends up with people who were invited and never granted.
//
// ══ TWO MODES: ORG-ISSUED, AND MODULE-ISSUED (D-CPO-24 clause 7) ═══════════════════════════════
//
// Opened from Team access (no `issuedModule`) it is the ORG-WIDE door: the Global Admin switch, the
// app picker over the catalog, every level. A Global Admin's own Team page inside an app opens the
// SAME door in place (OM-96, D-CPO-77), with that app already switched on and leading the list
// (`preselectModule`); the body is the org-wide door's, byte for byte. Opened from a module Owner's
// Team page (`issuedModule` = that app) it is the MODULE-ISSUED door the contract names: exactly that
// one app is offered — no picker — at a level no higher than the issuer's own tier in it, never
// Global Admin, and the body carries `issued_module`, which is what makes the server judge the
// offer under clause 7's scope at create AND again at accept. A module Owner holding
// `<module>:invite_admins` gets it and nothing wider, because nothing wider is theirs to offer; the
// line under it says what they can do for another app they own and who can give more, and sends
// nobody anywhere. A Global Admin never meets this door: their Team page is Team access in place. The
// cap is read off the issuer's own module ROW on the standing read, never off an admin flag — and the
// PANEL opens this door for an Owner-tier holder of the string only (the server refuses a
// Member-tier issuer outright), so the level here is a real choice; the one-option statement is the
// in-flight fallback, never a promise.
//
// ⚠ UNDER EVERY LEVEL, WHAT IT GIVES (OM-94): on both doors the line under an app's level lists what
// that level lets the person do there, from the app's own catalog (`LevelGives`), and follows the
// level. So both doors read the catalog; any admin may (`read_catalog` is the organization's
// baseline string).
//
// ⚠ ONLY THE APPS PEOPLE CAN OPEN HERE (OM-95): the picker is the grid's set (`isServedModule`),
// never the whole catalog the backend serves for the class.
//
// ⚠ THE FORM CANNOT BUILD AN OFFER THE SERVER WOULD REFUSE FOR ITS CONTENT. The offer rules —
// Global Admin carries no app list, a Member's list IS their access, a module-issued offer is that
// one app at a capped level — live in `org-invite-model.ts` as pure functions, so this file renders
// and edits a draft and never re-states a rule. The one place the two deliberately disagree is
// documented there.
//
// ⚠ AN ADDRESS THAT IS ALREADY AN ADMINISTRATOR HERE IS A SUCCESS, NOT AN ERROR, AND NOT AN INVITE.
// Giving someone who is already in more apps is a change to their access, never something to accept,
// so the server creates nothing and emails nobody and says so with `already_admin`. This dialog
// renders that as its own outcome — the one screen the composer could get catastrophically wrong is
// a cheerful "they have been invited" for a person who was never invited and will never receive
// anything.
//
// ⚠ THE EXTERNAL MARK (§OM-53) IS ASKED RIGHT UNDER THE ADDRESS, on both doors, under its own small
// label: who the person is comes before what they can open, so the switch never reads as one more
// app. On an app's own door, for an issuer who cannot manage the person's access afterwards, its line
// adds that only a Global Admin can change it later. The answer that drops it — an address already
// on the team — leads its remedy with the act when External was on.
//
// DATA CONTRACT: the `<prefix>-invite-composer-*` test-id family. Both doors carry `-external` (and
// its `-section` and `-switch`) and, under each offered app's level, `-module-<slug>-level-gives`;
// the org-wide door's offer is `-offer`. The module-issued mode adds `-issued-module`,
// `-module-<slug>-tier` (a choice) or `-module-<slug>-tier-fixed` (no choice) and
// `-who-can-give-more` (the Owner's last line, only where the organization has another app), and,
// on the already-admin outcome, `-already-admin-external` (the remedy that names the label). The
// wider door's `-team-access-line`, `-team-access-link` and `-already-admin-team-access-link` retired
// with it (OM-96).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  ErrorMessage,
  Field,
  Select,
  Switch,
  testIdProp,
} from '@givmo/design-system';
import { isServedModule, testIdPrefix, type OrgConsoleConfig } from './org-console-config';
import { catalogFor } from './org-access-model';
import { orgMemberName } from './org-decode';
import {
  buildInviteBody,
  emptyInviteDraft,
  inviteDraftProblems,
  issuerTierIn,
  moduleIssuedInviteDraft,
  reseatInviteDraft,
  tiersAtOrBelow,
  type InviteDraft,
  type InviteDraftProblem,
} from './org-invite-model';
import { isOrgInviteRateLimited } from './org-console-transport';
import { EXTERNAL_COPY, ExternalSection } from './org-external';
import { LevelGives } from './org-level-gives';
import { TEAM_COPY } from './org-team-copy';
import {
  GLOBAL_ADMIN_LABEL,
  MEMBER_LABEL,
  ROLE_MEMBER,
  ROLE_OWNER,
  globalAdminDescription,
  moduleTierLabel,
  type OrgTier,
} from './org-tiers';
import {
  useCreateOrgInvite,
  useOrgAccessCatalog,
  useOrgAccessGate,
  useOrgMembers,
} from './use-org-console';
import type { OrgModuleCatalogRow } from './wire-types';

export interface OrgInviteComposerDialogProps {
  config: OrgConsoleConfig;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * MODULE-ISSUED mode (D-CPO-24 clause 7): the app whose own Team page opened this composer. Set ⇒
   * exactly that one app is offered, at a level no higher than the issuer's own tier in it, never
   * Global Admin, and the create body carries `issued_module`. Absent ⇒ the org-wide door.
   */
  issuedModule?: string;
  /**
   * The ORG-WIDE door opened from inside an app (OM-96): a Global Admin on that app's Team page gets
   * the whole invite in place, with this app already switched on. Nothing else changes: the body is
   * the org-wide door's, byte for byte, and the app can be switched off like any other.
   */
  preselectModule?: string;
}

/** The copy for each draft problem. One sentence, naming what to do rather than what is wrong. */
const PROBLEM_COPY: Record<InviteDraftProblem, string> = {
  'email-missing': 'Enter the email address to invite.',
  'email-format': 'Invalid email format',
  'no-apps': 'Choose at least one app, or make them a Global Admin.',
};

export function OrgInviteComposerDialog({
  config,
  open,
  onOpenChange,
  issuedModule,
  preselectModule,
}: OrgInviteComposerDialogProps) {
  const prefix = `${testIdPrefix(config)}-invite-composer`;
  const issued = (issuedModule ?? '').trim();
  const moduleIssued = issued.length > 0;
  const preselect = moduleIssued ? '' : (preselectModule ?? '').trim();
  // The issuer's own standing — the cap on a module-issued level. The same `/me` query every panel
  // joins; never a second request.
  const gate = useOrgAccessGate(config.orgRef);
  // The catalog: the org-wide door's app picker, and on both doors what each level gives (OM-94).
  // Any admin may read it (`read_catalog` is the organization's baseline string).
  const catalogQuery = useOrgAccessCatalog(config.orgRef, open);
  // ⚠ ONLY THE APPS PEOPLE CAN OPEN HERE (OM-95): the grid's set, never the whole catalog.
  const catalog = useMemo(
    () => (catalogQuery.data ?? []).filter((row) => isServedModule(config, row.module)),
    [catalogQuery.data, config],
  );
  const create = useCreateOrgInvite(config.orgRef);
  /** The app to switch on once the catalog lands — consumed once per opening. */
  const preselectPending = useRef<string>('');

  const [draft, setDraft] = useState<InviteDraft>(() => emptyInviteDraft([]));
  const [showProblems, setShowProblems] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  /**
   * The address the server told us already administers this organization, and whether the person
   * inviting them had External on: the one choice that answer drops, so the outcome says where it
   * lives instead.
   */
  const [alreadyAdmin, setAlreadyAdmin] = useState<{ email: string; external: boolean } | null>(
    null,
  );
  /** Whether the previous render was open — so priming fires on the OPENING edge only. */
  const wasOpen = useRef(false);

  // Prime on open, in the mode the host opened it in. The catalog is a paged read the hook
  // exhausts, so for the org-wide door it usually lands AFTER this — hence the re-seat rather than a
  // rebuild: whatever has been chosen survives the arrival.
  //
  // ⚠ ON THE OPENING EDGE, NOT ON EVERY RENDER WHILE OPEN: the pre-existing draft is the person's
  // work, and a host re-render (an invite list refetching on window focus, say) must never reset it.
  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;
    setDraft(moduleIssued ? moduleIssuedInviteDraft(issued) : emptyInviteDraft([]));
    preselectPending.current = preselect;
    setShowProblems(false);
    setServerError(null);
    setAlreadyAdmin(null);
  }, [open, moduleIssued, issued, preselect]);

  // The org-wide door only: the module-issued draft's one row is the page's, not the catalog's to
  // rewrite. The app the page belongs to is switched on the first time its row arrives, and never
  // again in the same opening — after that, on or off is the person's choice.
  useEffect(() => {
    if (!open || moduleIssued) return;
    const pending = preselectPending.current;
    const arrived = pending.length > 0 && catalog.some((row) => row.module === pending);
    if (arrived) preselectPending.current = '';
    setDraft((current) => {
      const next = reseatInviteDraft(current, catalog);
      if (!arrived) return next;
      return {
        ...next,
        modules: next.modules.map((row) =>
          row.module === pending ? { ...row, offered: true } : row,
        ),
      };
    });
  }, [open, moduleIssued, catalog]);

  const problems = inviteDraftProblems(draft);
  const setModule = (module: string, next: Partial<{ offered: boolean; tier: OrgTier }>) =>
    setDraft((current) => ({
      ...current,
      modules: current.modules.map((row) => (row.module === module ? { ...row, ...next } : row)),
    }));

  // Clause 7's cap: the issuer's own tier in the app, off their module row.
  const levels = tiersAtOrBelow(issuerTierIn(gate.self, issued));
  const issuedLabel = moduleIssued ? config.vocab.moduleLabel(issued) : '';
  // The Owner's last line shows only where the organization has another app people can open here
  // (D-CPO-77 item 4), so it waits for the catalog: unread, it is left out rather than guessed.
  const anotherApp = moduleIssued && catalog.some((row) => row.module !== issued);

  const submit = (event?: React.FormEvent) => {
    event?.preventDefault();
    setServerError(null);
    const body = buildInviteBody(draft);
    if (body === null) {
      setShowProblems(true);
      return;
    }
    create.mutate(body, {
      onSuccess: (result) => {
        // ⚠ THE ANSWER DECIDES THE SCREEN, not the absence of an error. `already_admin` is the whole
        // reason the wire shape is discriminated: this branch is a success in which nothing was
        // created and nobody will receive anything.
        if (result !== null && result.already_admin) {
          setAlreadyAdmin({ email: draft.email.trim(), external: draft.external });
          return;
        }
        onOpenChange(false);
      },
      onError: (error: unknown) => {
        if (isOrgInviteRateLimited(error)) {
          setServerError(
            `This ${config.vocab.orgNoun} has invited as many people as it can this hour. Try again shortly.`,
          );
          return;
        }
        // ⚠ THE SERVER'S OWN TEXT, VERBATIM. Every other refusal on this operation is an ordinary
        // validation answer to an authenticated admin about a request they just made, and it NAMES
        // the reason — a rejected email domain, an app this organization's class does not carry, a
        // level that is not one of the two, a scope the issuer does not hold. Replacing that with
        // one generic sentence would throw away the only thing that tells them what to change.
        setServerError(
          error instanceof Error && error.message.length > 0
            ? error.message
            : 'Something went wrong. Please try again.',
        );
      },
    });
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <Dialog.Content
        aria-label={
          moduleIssued ? `Invite someone to ${issuedLabel}` : 'Invite someone to this organization'
        }
        className="flex max-h-[80vh] flex-col gap-16 overflow-y-auto"
        testId={`${prefix}-dialog`}
      >
        {alreadyAdmin !== null ? (
          <AlreadyAdminOutcome
            email={alreadyAdmin.email}
            external={alreadyAdmin.external}
            orgNoun={config.vocab.orgNoun}
            prefix={prefix}
            issuedLabel={issuedLabel}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <form className="flex flex-col gap-16" onSubmit={submit} noValidate>
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              Invite someone
            </Dialog.Title>
            <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              We’ll email an invite link to this address. It can be accepted once, by this email,
              whether or not they already have an account.
            </p>

            <Field
              label="Email address"
              type="email"
              autoComplete="email"
              value={draft.email}
              onChange={(e) => setDraft((current) => ({ ...current, email: e.target.value }))}
              testId={`${prefix}-email`}
            />

            {/* ── Who they are, then what they can open ─────────────────────────────────────
                The mark is about the person, so it comes first, under a label of its own that keeps
                it from reading as one more app. A module's Owner may mark the person they invite,
                and is told it is their one chance: afterwards only someone who manages that
                person's access can change it. */}
            <ExternalSection
              orgNoun={config.vocab.orgNoun}
              checked={draft.external}
              onCheckedChange={(checked) =>
                setDraft((current) => ({ ...current, external: checked }))
              }
              note={moduleIssued ? EXTERNAL_COPY.onlyAGlobalAdminLater : undefined}
              testId={`${prefix}-external`}
            />

            {moduleIssued ? (
              <>
                {/* ── The one app this page issues into, at a capped level ─────────────────── */}
                <ModuleIssuedOffer
                  config={config}
                  idPrefix={`${prefix}-module-${issued}`}
                  testId={`${prefix}-issued-module`}
                  module={issued}
                  label={issuedLabel}
                  tier={draft.modules.find((row) => row.module === issued)?.tier ?? ROLE_MEMBER}
                  levels={levels}
                  entry={catalogFor(catalogQuery.data ?? [], issued)}
                  onTierChange={(tier) => setModule(issued, { tier })}
                />
                {/* ⚠ NOTHING HERE SENDS ANYONE ELSEWHERE (OM-96). This door is a module Owner's; its
                    line says what they can do for another app they own and who can give more, never
                    where to go. Only where the organization has another app (D-CPO-77 item 4): with
                    this app alone, the line answered a question nobody inviting here asks. */}
                {anotherApp && (
                  <WhoCanGiveMore config={config} testId={`${prefix}-who-can-give-more`} />
                )}
              </>
            ) : (
              <div className="flex flex-col gap-12" {...testIdProp(`${prefix}-offer`)}>
                {/* ── Global Admin, then which apps (Q8, D-CPO-77 item 8) ─────────────────────
                    The Global Admin card sits ABOVE "Which apps they can open", where Team access's
                    invite had it before OM-53's M07 frame moved it under the heading: it is not an
                    app, and under the heading the user test's executive director was unsure what it
                    meant. Its own line now says what it means for people. */}
                <div className="flex items-start justify-between gap-16 rounded-lg border border-border-subtle p-16">
                  <div className="min-w-0">
                    <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {GLOBAL_ADMIN_LABEL}
                    </p>
                    <p className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                      {globalAdminDescription(config.vocab.orgNoun)}
                    </p>
                  </div>
                  <Switch
                    appearance="saas"
                    checked={draft.isGlobalAdmin}
                    onCheckedChange={(checked) =>
                      setDraft((current) => ({ ...current, isGlobalAdmin: checked }))
                    }
                    aria-label={GLOBAL_ADMIN_LABEL}
                    testId={`${prefix}-global-admin`}
                  />
                </div>
                <div {...testIdProp(`${prefix}-apps-heading`)}>
                  <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                    Which apps they can open
                  </p>
                  {/* The one sentence on this screen that says something the controls do not:
                      what is chosen here is the WHOLE of what this person gets. Nothing arrives
                      behind it later, and the level is per app rather than one setting for all of
                      them. */}
                  <p className="mt-2 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                    {TEAM_COPY.exactlyWhatYouChoose}
                  </p>
                </div>

                {draft.isGlobalAdmin ? (
                  <p
                    className="rounded-lg bg-surface-sunken p-16 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    {...testIdProp(`${prefix}-global-admin-notice`)}
                  >
                    A {GLOBAL_ADMIN_LABEL} has every app, at {ROLE_OWNER} level. Turn{' '}
                    {GLOBAL_ADMIN_LABEL} off to choose app by app.
                  </p>
                ) : (
                  <div className="flex flex-col gap-12" {...testIdProp(`${prefix}-modules`)}>
                    {/* The app whose page opened the invite leads the list (OM-96), then the rest
                        in the catalog's order: the choice already made sits where it is seen. */}
                    {(preselect.length === 0
                      ? draft.modules
                      : [
                          ...draft.modules.filter((row) => row.module === preselect),
                          ...draft.modules.filter((row) => row.module !== preselect),
                        ]
                    ).map((row) => (
                      <ModuleOffer
                        key={row.module}
                        config={config}
                        idPrefix={`${prefix}-module-${row.module}`}
                        label={config.vocab.moduleLabel(row.module)}
                        row={row}
                        entry={catalogFor(catalog, row.module)}
                        onChange={(next) => setModule(row.module, next)}
                      />
                    ))}
                    {draft.modules.length === 0 && (
                      <p
                        className="font-sans text-s13 text-text-tertiary"
                        {...testIdProp(`${prefix}-no-modules`)}
                      >
                        {catalogQuery.isPending
                          ? 'Loading your apps…'
                          : `This ${config.vocab.orgNoun} has no apps to offer yet.`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {showProblems && problems.length > 0 && (
              <ErrorMessage testId={`${prefix}-problems`}>
                {problems.map((problem) => PROBLEM_COPY[problem]).join(' ')}
              </ErrorMessage>
            )}
            {serverError !== null && (
              <ErrorMessage testId={`${prefix}-error`}>{serverError}</ErrorMessage>
            )}

            <div className="mt-8 flex items-center justify-end gap-8">
              <Dialog.Close
                disabled={create.isPending}
                render={<Button appearance="saas" tone="secondary" size="md" />}
                {...testIdProp(`${prefix}-cancel`)}
              >
                Cancel
              </Dialog.Close>
              {/* ⚠ ENABLED EVEN WHILE THE DRAFT IS INCOMPLETE, unlike the access editor's Save. That
                  control is disabled because "nothing changed" is a real, readable state; here a
                  disabled button in a form with three separate requirements just goes quiet and
                  leaves a person guessing which one it is waiting on. Pressing it names them. */}
              <Button
                type="submit"
                appearance="saas"
                tone="primary"
                size="md"
                loading={create.isPending}
                testId={`${prefix}-submit`}
              >
                Invite them
              </Button>
            </div>
          </form>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

interface ModuleOfferProps {
  config: OrgConsoleConfig;
  idPrefix: string;
  label: string;
  row: { module: string; offered: boolean; tier: OrgTier };
  /** The app's catalog row, for what the chosen level gives (OM-94). */
  entry: OrgModuleCatalogRow | undefined;
  onChange: (next: Partial<{ offered: boolean; tier: OrgTier }>) => void;
}

/**
 * One app's offer on the org-wide door: whether they get it, and at what level.
 *
 * The level control appears only once the app is offered — a level for something they do not hold
 * is a control with no subject, and the access editor makes the same choice for the same reason. Under
 * it, what that level gives in the app (OM-94), the same words as the app's own door.
 */
function ModuleOffer({ config, idPrefix, label, row, entry, onChange }: ModuleOfferProps) {
  return (
    <section className="rounded-lg border border-border-subtle p-16" {...testIdProp(idPrefix)}>
      <div className="flex items-center justify-between gap-16">
        <p className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
          {label}
        </p>
        <Switch
          appearance="saas"
          checked={row.offered}
          onCheckedChange={(checked) => onChange({ offered: checked })}
          aria-label={label}
          testId={`${idPrefix}-offer`}
        />
      </div>
      {row.offered && (
        <div className="mt-16 flex flex-col gap-12 border-t border-border-subtle pt-16">
          <Select
            appearance="saas"
            label="Level"
            value={row.tier}
            onValueChange={(value) =>
              onChange({ tier: value === ROLE_OWNER ? ROLE_OWNER : ROLE_MEMBER })
            }
            options={[
              { value: ROLE_MEMBER, label: MEMBER_LABEL },
              { value: ROLE_OWNER, label: ROLE_OWNER },
            ]}
            testId={`${idPrefix}-tier`}
          />
          <LevelGives
            config={config}
            module={row.module}
            tier={row.tier}
            entry={entry}
            testId={`${idPrefix}-level-gives`}
          />
        </div>
      )}
    </section>
  );
}

interface ModuleIssuedOfferProps {
  config: OrgConsoleConfig;
  idPrefix: string;
  testId: string;
  module: string;
  label: string;
  tier: OrgTier;
  /** The levels the issuer may offer — clause 7's cap, already applied. */
  levels: readonly OrgTier[];
  /** The app's catalog row, for what the chosen level gives (OM-94). */
  entry: OrgModuleCatalogRow | undefined;
  onTierChange: (tier: OrgTier) => void;
}

/**
 * The one app a MODULE-ISSUED offer carries, at a level the issuer may give.
 *
 * No switch: the app is the page's own and is always offered, so a toggle here would be a control
 * that cannot be turned off. The level is a choice whenever the door is open for real — the panel
 * opens it for an Owner-tier holder of the string only, whose cap is Owner. The one-option statement
 * covers the window before the issuer's standing has settled, and promises nothing about what may be
 * offered: the server, not this form, decides that at create. Under the level, what it gives
 * (OM-94), once the catalog is read.
 */
function ModuleIssuedOffer({
  config,
  idPrefix,
  testId,
  module,
  label,
  tier,
  levels,
  entry,
  onTierChange,
}: ModuleIssuedOfferProps) {
  return (
    <section
      className="flex flex-col gap-12 rounded-lg border border-border-subtle p-16"
      {...testIdProp(testId)}
    >
      <p className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{label}</p>
      {levels.length > 1 ? (
        <Select
          appearance="saas"
          label="Level"
          value={tier}
          onValueChange={(value) => onTierChange(value === ROLE_OWNER ? ROLE_OWNER : ROLE_MEMBER)}
          options={levels.map((level) => ({ value: level, label: moduleTierLabel(level) }))}
          testId={`${idPrefix}-tier`}
        />
      ) : (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          {...testIdProp(`${idPrefix}-tier-fixed`)}
        >
          Level: {moduleTierLabel(levels[0] ?? ROLE_MEMBER)}
        </p>
      )}
      {/* What the chosen level gives — it follows the select (OM-94). */}
      <LevelGives
        config={config}
        module={module}
        tier={tier}
        entry={entry}
        testId={`${idPrefix}-level-gives`}
      />
    </section>
  );
}

interface WhoCanGiveMoreProps {
  config: OrgConsoleConfig;
  testId: string;
}

/**
 * A module Owner's line under their invite, where the organization has other apps (OM-96): what they
 * CAN do for an app they own, then what only a Global Admin can, then who that is (`TEAM_COPY`).
 *
 * ⚠ IT NAMES PEOPLE AND THE APP'S OWN PAGE, NEVER THE ADMIN CONSOLE. A module Owner has no Admin
 * Console; the useful answers to "what if they need more?" are that an app they own has its own
 * Team page, and who the Global Admin is. The Global Admins come from the roster this page already
 * reads (the panel's own query, no second request), and only from a WHOLE one: unread, partly read
 * (a next page still to come could hold another) or naming none, the last sentence is left out
 * rather than wrong.
 *
 * ⚠ THE ROSTER'S `is_global_admin` IS READ HERE AS A NAME, NEVER AS ACCESS: no capability can say who
 * someone else is (the build law's allowlist carries this read with that reason).
 */
function WhoCanGiveMore({ config, testId }: WhoCanGiveMoreProps) {
  const roster = useOrgMembers(config.orgRef);
  const whole = roster.isSuccess && !roster.hasNextPage;
  const admins = whole
    ? (roster.data ?? []).filter((member) => member.is_global_admin).map(orgMemberName)
    : [];
  return (
    <p
      className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
      {...testIdProp(testId)}
    >
      {TEAM_COPY.whoCanGiveMore(admins)}
    </p>
  );
}

interface AlreadyAdminOutcomeProps {
  email: string;
  /** Whether External was on in the form: the choice this answer drops (§OM-53 §3.3). */
  external: boolean;
  orgNoun: string;
  prefix: string;
  /** MODULE-ISSUED mode: the app's label; '' on the org-wide door. */
  issuedLabel: string;
  onClose: () => void;
}

/**
 * The address already administers this organization.
 *
 * ⚠ IT SAYS WHAT DID NOT HAPPEN, AND WHERE THE ACT THEY WANTED ACTUALLY LIVES. Nothing was created
 * and nothing will arrive in anybody's mail, so the one thing this screen must not do is read like a
 * completed invite. On the org-wide door — Team access, or a Global Admin's app Team page in place —
 * the remedy is on the page behind this dialog: the whole team, and that person's own access, which
 * is why it is named rather than linked (a second control here would be a second door to a row that
 * is already on screen). On a module Owner's app door the roster is NARROWED to the app, where a
 * person who does not yet hold it is not, so the remedy is the door that is theirs: the person asks
 * for the app from the app menu, and this page's Access requests section is where they approve it
 * (R9).
 */
function AlreadyAdminOutcome({
  email,
  external,
  orgNoun,
  prefix,
  issuedLabel,
  onClose,
}: AlreadyAdminOutcomeProps) {
  // ⚠ WITH EXTERNAL ON, THE REMEDY LEADS WITH THE LABEL: nothing was created, so the label was not
  // set either, and an outcome that stayed silent would drop the choice. Where the person inviting
  // can set it themselves, the remedy starts with that act; on a module Owner's door, where only a
  // Global Admin can, it says so and names no page the person cannot see.
  const remedy = external ? (
    <span {...testIdProp(`${prefix}-already-admin-external`)}>
      {issuedLabel.length === 0
        ? EXTERNAL_COPY.alreadyAdminTeamList
        : EXTERNAL_COPY.alreadyAdminOnlyAGlobalAdmin}
    </span>
  ) : null;
  return (
    <div className="flex flex-col gap-16" {...testIdProp(`${prefix}-already-admin`)}>
      <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
        They already administer this {orgNoun}
      </Dialog.Title>
      <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        {issuedLabel.length === 0 ? (
          <>
            {email} is already on this {orgNoun}’s team, so there is nothing to accept and no invite
            went out.{' '}
            {remedy ?? (
              <>
                To change which apps they can open, find them in the team list and manage their
                access.
              </>
            )}
          </>
        ) : (
          <>
            {email} already administers this {orgNoun}, so no invite went out. They can request{' '}
            {issuedLabel} from the app menu, and you can approve it here.
            {remedy !== null && <> {remedy}</>}
          </>
        )}
      </p>
      <div className="mt-8 flex items-center justify-end">
        <Button
          appearance="saas"
          tone="primary"
          size="md"
          onClick={onClose}
          testId={`${prefix}-already-admin-close`}
        >
          Close
        </Button>
      </div>
    </div>
  );
}
